import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Form, Input, Popover } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { CALENDAR_VARIANTS, DateSelector } from "ui/BookingCalendar"
import styles from "./BookingForm.module.css"

const FIELD_FORMAT = "MM-DD-YYYY"

// What the field shows: the date, a fixed trip's first to last day, or a dynamic trip's range.
const formatSelection = ({ value, rangeValue, isRange, spanDays }) => {
  if (isRange) {
    return [rangeValue?.from, rangeValue?.to].filter(Boolean).map((date) => dayjs(date).format(FIELD_FORMAT)).join(" – ")
  }
  if (!value) return ""
  const start = dayjs(value)
  return spanDays >= 2 ? `${start.format(FIELD_FORMAT)} – ${start.add(spanDays - 1, "day").format(FIELD_FORMAT)}` : start.format(FIELD_FORMAT)
}

// A range is chosen once it has two different days; react-day-picker can report the first click
// as a one-day range.
const isSelectionComplete = (selection, isRange) =>
  isRange ? !!selection?.to && !dayjs(selection.from).isSame(selection.to, "day") : !!selection

// New Booking's date (redesign, Figma 7278:213634): a "mm-dd-yyyy" field that opens the booking
// calendar as a two-month popup, closing once the date (or the whole range) is picked. Takes the
// same props as DateSelector, which keeps every availability, span and range rule.
const DateField = ({ label, error, errorMessage, ...selectorProps }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const calendarRef = useRef(null)
  const { isRange, onChange } = selectorProps

  const handleChange = (selection) => {
    onChange(selection)
    if (isSelectionComplete(selection, isRange)) setIsOpen(false)
  }

  return (
    <Form.Item
      label={label}
      required
      validateStatus={error ? "error" : undefined}
      help={error ? errorMessage : undefined}
      className={styles.dateField}
    >
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        trigger="click"
        placement="bottomLeft"
        // Always below the field, as designed; it scrolls with the form rather than flipping up
        // over the fields above it.
        autoAdjustOverflow={false}
        arrow={false}
        destroyTooltipOnHide
        getPopupContainer={(trigger) => trigger.parentElement}
        overlayClassName={styles.datePopover}
        // The popup opens inside the form's scroll area, often past its bottom edge (always on a
        // phone): bring the calendar into view.
        afterOpenChange={(isNowOpen) => isNowOpen && calendarRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })}
        content={
          <div ref={calendarRef}>
            <DateSelector {...selectorProps} onChange={handleChange} variant={CALENDAR_VARIANTS.POPUP} />
          </div>
        }
      >
        <Input
          readOnly
          value={formatSelection(selectorProps)}
          placeholder={t("booking.date_field.placeholder")}
          suffix={<Icon type="feather" name="Calendar" width={20} height={20} />}
          status={error ? "error" : undefined}
          className={styles.dateFieldInput}
        />
      </Popover>
    </Form.Item>
  )
}

DateField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  isRange: PropTypes.bool,
  rangeValue: PropTypes.shape({ from: PropTypes.instanceOf(Date), to: PropTypes.instanceOf(Date) }),
  spanDays: PropTypes.number,
  // Passed through to DateSelector, as there.
  trip: PropTypes.object,
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  maxSpanByDate: PropTypes.objectOf(PropTypes.number),
  availableDates: PropTypes.arrayOf(PropTypes.string),
  bookedDates: PropTypes.arrayOf(PropTypes.string),
  blockedReasons: PropTypes.objectOf(PropTypes.string),
  allowPastDates: PropTypes.bool,
  onMonthChange: PropTypes.func
}

DateField.defaultProps = {
  error: false,
  errorMessage: undefined,
  value: null,
  isRange: false,
  rangeValue: undefined,
  spanDays: 0,
  trip: undefined,
  minDays: undefined,
  maxDays: undefined,
  maxSpanByDate: undefined,
  availableDates: undefined,
  bookedDates: undefined,
  blockedReasons: undefined,
  allowPastDates: false,
  onMonthChange: undefined
}

export default DateField
